import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Button, Typography, Paper, Grid, MenuItem, IconButton, Card,
    TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
    TextField, Stack, Checkbox, FormControlLabel, Divider, List, ListItem, ListItemText, CircularProgress
} from '@mui/material';
import {
    collection, getDocs, query, where, addDoc,
    deleteDoc, doc, updateDoc, onSnapshot, setDoc, orderBy, writeBatch
} from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { Delete, Edit, Save, Coffee, ContentCopy, Schedule, Block, Print } from '@mui/icons-material';
import { toast, ToastContainer } from 'react-toastify';

const ALL_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const TimetableManager = () => {
    const uid = auth.currentUser?.uid;
    const [schoolId, setSchoolId] = useState('');
    const [entries, setEntries] = useState([]);
    const [allocations, setAllocations] = useState([]);
    const [classes, setClasses] = useState([]);
    const [teacherMap, setTeacherMap] = useState({});
    const [loading, setLoading] = useState(true);

    const [selectedClass, setSelectedClass] = useState('');
    const [settings, setSettings] = useState({ breaks: [], holidays: [] });

    // Copy Tool State
    const [copySource, setCopySource] = useState('Monday');
    const [copyTarget, setCopyTarget] = useState('Tuesday');

    // Forms
    const [formData, setFormData] = useState({
        id: '', subject: '', teacherId: '', day: 'Monday', startTime: '08:00', endTime: '09:00'
    });
    const [breakForm, setBreakForm] = useState({ id: null, name: '', start: '11:00', end: '11:30' });

    // --- 1. INITIAL LOAD ---
    useEffect(() => {
        const loadInitialData = async () => {
            if (!uid) return;
            try {
                const userSnap = await getDocs(query(collection(db, 'users'), where('__name__', '==', uid)));
                if (userSnap.empty) return;
                const sId = userSnap.docs[0].data().schoolId;
                setSchoolId(sId);

                // Teachers
                const tSnap = await getDocs(query(collection(db, 'users'), where('schoolId', '==', sId), where('role', '==', 'Teacher')));
                const tObj = {};
                tSnap.docs.forEach(d => tObj[d.id] = d.data().name);
                setTeacherMap(tObj);

                // Classes
                const cSnap = await getDocs(query(collection(db, 'courses'), where('schoolId', '==', sId), orderBy('sequence', 'asc')));
                const classesData = cSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                setClasses(classesData);
                if (classesData.length > 0) setSelectedClass(classesData[0].name);

                // Settings
                const setSnap = await getDocs(query(collection(db, 'school_settings'), where('schoolId', '==', sId)));
                if (!setSnap.empty) setSettings({ breaks: setSnap.docs[0].data().breaks || [], holidays: setSnap.docs[0].data().holidays || [] });

                // Allocations
                const aSnap = await getDocs(query(collection(db, 'teacher_allocations'), where('schoolId', '==', sId)));
                setAllocations(aSnap.docs.map(d => ({ id: d.id, ...d.data() })));

                setLoading(false);
            } catch (err) { setLoading(false); }
        };
        loadInitialData();
    }, [uid]);

    // --- 2. SYNC TIMETABLE ---
    useEffect(() => {
        if (!schoolId || !selectedClass) return;
        const q = query(collection(db, "timetables"), where("schoolId", "==", schoolId), where("className", "==", selectedClass));
        return onSnapshot(q, (snap) => {
            const data = snap.docs.map(d => ({ ...d.data(), id: d.id }));
            setEntries(data.sort((a, b) => a.startTime.localeCompare(b.startTime)));
        });
    }, [schoolId, selectedClass]);

    const allTimeSlots = useMemo(() => {
        const slots = new Set();
        entries.forEach(e => slots.add(`${e.startTime}-${e.endTime}`));
        settings.breaks?.forEach(b => slots.add(`${b.start}-${b.end}`));
        return Array.from(slots).sort((a, b) => a.split('-')[0].localeCompare(b.split('-')[0]));
    }, [entries, settings.breaks]);

    // --- 3. ACTIONS ---
    const handleSaveEntry = async () => {
        if (!formData.teacherId || !formData.subject) return toast.warning("Select Subject/Teacher");
        try {
            const payload = { ...formData, className: selectedClass, schoolId };
            formData.id ? await updateDoc(doc(db, "timetables", formData.id), payload) : await addDoc(collection(db, "timetables"), payload);
            setFormData({ id: '', subject: '', teacherId: '', day: 'Monday', startTime: '08:00', endTime: '09:00' });
            toast.success("Period Saved");
        } catch (e) { toast.error("Error saving period"); }
    };

    const handleSaveBreak = async () => {
        if (!breakForm.name) return toast.warning("Enter break name");
        let updatedBreaks = [...settings.breaks];
        if (breakForm.id !== null) updatedBreaks[breakForm.id] = { name: breakForm.name, start: breakForm.start, end: breakForm.end };
        else updatedBreaks.push({ name: breakForm.name, start: breakForm.start, end: breakForm.end });

        const newSet = { ...settings, breaks: updatedBreaks };
        setSettings(newSet);
        await setDoc(doc(db, "school_settings", schoolId), { ...newSet, schoolId });
        setBreakForm({ id: null, name: '', start: '11:00', end: '11:30' });
        toast.success("Break Updated");
    };

    const handleCopyDay = async () => {
        const sourceSlots = entries.filter(e => e.day === copySource);
        if (sourceSlots.length === 0) return toast.error("No data to copy");
        if (!window.confirm(`Copy ${copySource} to ${copyTarget}?`)) return;
        try {
            const batch = writeBatch(db);
            entries.filter(e => e.day === copyTarget).forEach(e => batch.delete(doc(db, "timetables", e.id)));
            sourceSlots.forEach(s => {
                const newRef = doc(collection(db, "timetables"));
                const { id, ...data } = s;
                batch.set(newRef, { ...data, day: copyTarget });
            });
            await batch.commit();
            toast.success("Copied Successfully");
        } catch (e) { toast.error("Copy Failed"); }
    };

    const toggleHoliday = async (day) => {
        const h = settings.holidays.includes(day) ? settings.holidays.filter(d => d !== day) : [...settings.holidays, day];
        const newSet = { ...settings, holidays: h };
        setSettings(newSet);
        await setDoc(doc(db, "school_settings", schoolId), { ...newSet, schoolId });
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ p: 1, bgcolor: '#f4f7f9', minHeight: '100vh', fontSize: '0.8rem' }}>
            <ToastContainer autoClose={1000} />

            {/* HEADER */}
            <Paper className="no-print" sx={{ p: 1, mb: 1, display: 'flex', justifyContent: 'space-between', bgcolor: '#1a237e', color: 'white', alignItems: 'center' }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Typography sx={{ fontWeight: 900 }}>TIMETABLE MANAGER</Typography>
                    <TextField select size="small" value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}
                        sx={{ bgcolor: 'white', borderRadius: 1, width: 140, "& .MuiInputBase-input": { py: 0.5, fontSize: '0.75rem' } }}>
                        {classes.map(c => <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>)}
                    </TextField>
                </Stack>
                <Button variant="contained" size="small" color="info" startIcon={<Print />} onClick={() => window.print()}>Print</Button>
            </Paper>

            <Grid className="row" spacing={1}>
                {/* LEFT SIDEBAR: CONTROLS */}
                <Grid item xs={12} className="col-sm-3 no-print">
                    {/* ADD PERIOD */}
                    <Card sx={{ p: 1.5, mb: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 900, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Schedule fontSize="inherit"/> ADD PERIOD
                        </Typography>
                        <Stack spacing={1} mt={1}>
                            <TextField select size="small" label="Day" value={formData.day} onChange={e => setFormData({ ...formData, day: e.target.value })}>
                                {ALL_DAYS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                            </TextField>
                            <TextField select size="small" label="Subject & Teacher" value={formData.teacherId ? `${formData.teacherId}|${formData.subject}` : ''}
                                onChange={e => { const [tId, sub] = e.target.value.split('|'); setFormData({ ...formData, teacherId: tId, subject: sub }); }}>
                                {allocations?.filter(a => a.classId === selectedClass).map(af =>
                                    af.subjects?.map(s => <MenuItem key={af.teacherId + s} value={`${af.teacherId}|${s}`}>{s} — {teacherMap[af.teacherId] || 'Staff'}</MenuItem>))}
                            </TextField>
                            <Stack direction="row" spacing={1}>
                                <TextField type="time" size="small" label="Start" InputLabelProps={{ shrink: true }} value={formData.startTime} onChange={e => setFormData({ ...formData, startTime: e.target.value })} />
                                <TextField type="time" size="small" label="End" InputLabelProps={{ shrink: true }} value={formData.endTime} onChange={e => setFormData({ ...formData, endTime: e.target.value })} />
                            </Stack>
                            <Button fullWidth variant="contained" size="small" onClick={handleSaveEntry} startIcon={<Save />}>Save Period</Button>
                        </Stack>
                    </Card>

                    {/* COPY TOOL */}
                    <Card sx={{ p: 1.5, mb: 1, border: '1px solid #c8e6c9' }}>
                        <Typography variant="caption" sx={{ fontWeight: 900, color: 'green', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ContentCopy fontSize="inherit"/> COPY DAY SCHEDULE
                        </Typography>
                        <Stack direction="row" spacing={1} mt={1} alignItems="center">
                            <TextField select size="small" fullWidth value={copySource} onChange={e => setCopySource(e.target.value)}>
                                {ALL_DAYS.map(d => <MenuItem key={d} value={d}>{d.substring(0,3)}</MenuItem>)}
                            </TextField>
                            <Typography variant="caption">To</Typography>
                            <TextField select size="small" fullWidth value={copyTarget} onChange={e => setCopyTarget(e.target.value)}>
                                {ALL_DAYS.map(d => <MenuItem key={d} value={d}>{d.substring(0,3)}</MenuItem>)}
                            </TextField>
                            <IconButton color="success" onClick={handleCopyDay}><Save fontSize="small"/></IconButton>
                        </Stack>
                    </Card>

                    {/* MANAGE BREAKS */}
                    <Card sx={{ p: 1.5, mb: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 900, color: '#f57f17', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Coffee fontSize="inherit"/> BREAKS
                        </Typography>
                        <Stack spacing={1} mt={1}>
                            <TextField size="small" label="Break Label" value={breakForm.name} onChange={e => setBreakForm({ ...breakForm, name: e.target.value })} />
                            <Stack direction="row" spacing={1}>
                                <TextField type="time" size="small" value={breakForm.start} onChange={e => setBreakForm({ ...breakForm, start: e.target.value })} />
                                <TextField type="time" size="small" value={breakForm.end} onChange={e => setBreakForm({ ...breakForm, end: e.target.value })} />
                            </Stack>
                            <Button fullWidth variant="outlined" size="small" color="warning" onClick={handleSaveBreak}>Save Break</Button>
                            <List sx={{ maxHeight: 100, overflow: 'auto' }}>
                                {settings.breaks.map((b, i) => (
                                    <ListItem key={i} sx={{ p: 0, borderBottom: '1px solid #eee' }}>
                                        <ListItemText primary={<Typography sx={{fontSize: '0.65rem'}}>{b.name} ({b.start}-{b.end})</Typography>} />
                                        <IconButton size="small" onClick={() => setBreakForm({...b, id: i})}><Edit sx={{fontSize: 12}}/></IconButton>
                                    </ListItem>
                                ))}
                            </List>
                        </Stack>
                    </Card>

                    {/* HOLIDAYS */}
                    <Card sx={{ p: 1.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 900 }}>WEEKLY HOLIDAYS</Typography>
                        <Box display="flex" flexWrap="wrap">
                            {ALL_DAYS.map(d => (
                                <FormControlLabel key={d} control={<Checkbox size="small" checked={settings.holidays.includes(d)} onChange={() => toggleHoliday(d)} />} 
                                label={<Typography sx={{fontSize: '0.65rem'}}>{d.substring(0,3)}</Typography>} />
                            ))}
                        </Box>
                    </Card>
                </Grid>

                {/* MAIN TABLE */}
                <Grid item xs={12}className="col-sm-9" id="printable-area">
                    <TableContainer component={Paper} sx={{ borderRadius: 1 }}>
                        <Box p={1} textAlign="center" sx={{ bgcolor: '#eee' }}>
                            <Typography sx={{ fontWeight: 900, color: '#1a237e' }}>{selectedClass} TIMETABLE</Typography>
                        </Box>
                        <Table size="small" sx={{ tableLayout: 'fixed' }}>
                            <TableHead sx={{ bgcolor: '#263238' }}>
                                <TableRow>
                                    <TableCell sx={{ color: 'white', width: 75, textAlign: 'center', fontWeight: 900 }}>TIME</TableCell>
                                    {ALL_DAYS.map(day => (
                                        <TableCell key={day} align="center" sx={{ 
                                            color: 'white', fontWeight: 900, fontSize: '0.7rem',
                                            bgcolor: settings.holidays.includes(day) ? '#d32f2f' : 'inherit'
                                        }}>{day.toUpperCase()}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {allTimeSlots.map(timeRange => {
                                    const [start, end] = timeRange.split('-');
                                    const breakItem = settings.breaks?.find(b => b.start === start && b.end === end);
                                    return (
                                        <TableRow key={timeRange} sx={{ height: breakItem ? 40 : 80 }}>
                                            <TableCell sx={{ bgcolor: '#f5f5f5', textAlign: 'center', p: 0.5, borderRight: '1px solid #ddd' }}>
                                                <Typography sx={{ fontSize: '0.65rem', fontWeight: 900 }}>{start}</Typography>
                                                <Typography sx={{ fontSize: '0.55rem', color: '#666' }}>{end}</Typography>
                                            </TableCell>
                                            {ALL_DAYS.map(day => {
                                                const cellData = entries.find(e => e.day === day && e.startTime === start && e.endTime === end);
                                                if (breakItem) return (
                                                    <TableCell key={day} align="center" sx={{ bgcolor: '#fffde7', borderBottom: '2px solid #fbc02d' }}>
                                                        <Typography sx={{ fontSize: '0.6rem', fontWeight: 900, color: '#f57f17' }}>{breakItem.name}</Typography>
                                                    </TableCell>
                                                );
                                                return (
                                                    <TableCell key={day} align="center" sx={{ p: 0.5, border: '0.1px solid #eee' }}>
                                                        {cellData ? (
                                                            <Box sx={{ p: 0.5, bgcolor: '#e3f2fd', borderRadius: 1, borderLeft: '3px solid #1976d2' }}>
                                                                <Typography sx={{ fontSize: '0.7rem', fontWeight: 900 }}>{cellData.subject}</Typography>
                                                                <Typography sx={{ fontSize: '0.55rem' }}>{teacherMap[cellData.teacherId]?.split(' ')[0]}</Typography>
                                                                <Stack direction="row" justifyContent="center" className="no-print">
                                                                    <IconButton size="small" onClick={() => setFormData(cellData)} sx={{p:0}}><Edit sx={{fontSize: 12}}/></IconButton>
                                                                    <IconButton size="small" color="error" onClick={() => deleteDoc(doc(db, 'timetables', cellData.id))} sx={{p:0}}><Delete sx={{fontSize: 12}}/></IconButton>
                                                                </Stack>
                                                            </Box>
                                                        ) : (
                                                            settings.holidays.includes(day) && <Block sx={{ color: '#eee', fontSize: 14 }} />
                                                        )}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        </Box>
    );
};

export default TimetableManager;